import React from 'react';
import LoadingContainer from '../../../shared/ui/loading-container/loading-container.jsx';
import { LEGAL_DOCUMENT_STATUS_OPTIONS } from '../model/system-legal.model.js';

function LegalDocumentsTab({
  documents,
  activeDocumentCount,
  uploadForm,
  uploadErrors,
  setUploadField,
  uploadDocument,
  downloadDocument,
  isLoading,
  isSaving,
  isDownloadingUid,
  documentsLoadError,
}) {
  const hasRows = documents.length > 0;

  return (
    <div className="row g-3">
      <div className="col-12">
        {!isLoading && !documentsLoadError && activeDocumentCount === 0 ? (
          <div className="alert alert-warning d-flex align-items-center gap-2" role="alert">
            <i className="ti ti-alert-triangle" aria-hidden="true" />
            <span>No ACTIVE legal documents exist.</span>
          </div>
        ) : null}
      </div>

      <div className="col-xl-4">
        <div className="card card-body h-100">
          <h5 className="mb-3">Upload Document</h5>
          <form onSubmit={uploadDocument}>
            <div className="mb-3">
              <label className="form-label" htmlFor="legal-document-name">Name *</label>
              <input
                id="legal-document-name"
                className={`form-control ${uploadErrors.name ? 'is-invalid' : ''}`}
                value={uploadForm.name}
                onChange={(event) => setUploadField('name', event.target.value)}
                disabled={isSaving}
              />
              {uploadErrors.name ? <div className="invalid-feedback">{uploadErrors.name}</div> : null}
            </div>

            <div className="row g-2">
              <div className="col-md-6">
                <label className="form-label" htmlFor="legal-document-version">Version *</label>
                <input
                  id="legal-document-version"
                  className={`form-control ${uploadErrors.version ? 'is-invalid' : ''}`}
                  value={uploadForm.version}
                  onChange={(event) => setUploadField('version', event.target.value)}
                  disabled={isSaving}
                />
                {uploadErrors.version ? <div className="invalid-feedback">{uploadErrors.version}</div> : null}
              </div>
              <div className="col-md-6">
                <label className="form-label" htmlFor="legal-document-status">Status *</label>
                <select
                  id="legal-document-status"
                  className={`form-select ${uploadErrors.status ? 'is-invalid' : ''}`}
                  value={uploadForm.status}
                  onChange={(event) => setUploadField('status', event.target.value)}
                  disabled={isSaving}
                >
                  {LEGAL_DOCUMENT_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                {uploadErrors.status ? <div className="invalid-feedback">{uploadErrors.status}</div> : null}
              </div>
            </div>

            <div className="row g-2 mt-1">
              <div className="col-md-6">
                <label className="form-label" htmlFor="legal-document-effective-date">Effective date</label>
                <input
                  id="legal-document-effective-date"
                  type="date"
                  className={`form-control ${uploadErrors.effectiveDate ? 'is-invalid' : ''}`}
                  value={uploadForm.effectiveDate}
                  onChange={(event) => setUploadField('effectiveDate', event.target.value)}
                  disabled={isSaving}
                />
                {uploadErrors.effectiveDate ? <div className="invalid-feedback">{uploadErrors.effectiveDate}</div> : null}
              </div>
              <div className="col-md-6">
                <label className="form-label" htmlFor="legal-document-expiration-date">Expiration date</label>
                <input
                  id="legal-document-expiration-date"
                  type="date"
                  className={`form-control ${uploadErrors.expirationDate ? 'is-invalid' : ''}`}
                  value={uploadForm.expirationDate}
                  onChange={(event) => setUploadField('expirationDate', event.target.value)}
                  disabled={isSaving}
                />
                {uploadErrors.expirationDate ? <div className="invalid-feedback">{uploadErrors.expirationDate}</div> : null}
              </div>
            </div>

            <div className="mt-3">
              <label className="form-label" htmlFor="legal-document-file">Document *</label>
              <input
                id="legal-document-file"
                type="file"
                className={`form-control ${uploadErrors.document ? 'is-invalid' : ''}`}
                onChange={(event) => setUploadField('document', event.target.files?.[0] || null)}
                disabled={isSaving}
              />
              {uploadErrors.document ? <div className="invalid-feedback">{uploadErrors.document}</div> : null}
            </div>

            <button type="submit" className="btn btn-primary w-100 mt-4" disabled={isSaving}>
              <i className="ti ti-upload me-2" aria-hidden="true" />
              Upload
            </button>
          </form>
        </div>
      </div>

      <div className="col-xl-8">
        <div className="card card-body h-100">
          <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
            <h5 className="mb-0">Documents</h5>
            <span className="badge bg-light-subtle text-muted">{activeDocumentCount} active</span>
          </div>

          <LoadingContainer isLoading={isLoading} hasData={hasRows}>
            <table className="table search-table align-middle text-nowrap mb-0">
              <thead className="header-item">
                <tr>
                  <th>Name</th>
                  <th>Version</th>
                  <th>Status</th>
                  <th>Effective date</th>
                  <th>Expiration date</th>
                  <th className="text-end" style={{ width: '1%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {hasRows ? documents.map((row) => (
                  <tr key={row.key}>
                    <td>{row.name || '-'}</td>
                    <td>{row.version || '-'}</td>
                    <td><span className={row.statusBadgeClass}>{row.statusLabel}</span></td>
                    <td>{row.effectiveDateLabel || '-'}</td>
                    <td>{row.expirationDateLabel || '-'}</td>
                    <td className="text-end">
                      {row.canDownload ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => downloadDocument(row)}
                          disabled={isDownloadingUid === row.uid}
                          title="Download"
                        >
                          <i className="ti ti-download" aria-hidden="true" />
                        </button>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="6" className="text-center text-muted">No legal documents uploaded.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </LoadingContainer>
        </div>
      </div>
    </div>
  );
}

export default LegalDocumentsTab;
